import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  GetApp as ExportIcon,
  LocalShipping as ShippingIcon,
  Business as BusinessIcon,
  Public as ExportInfoIcon,
  Storefront as ProductsIcon,
  Assessment as ReportIcon,
} from '@material-ui/icons';
import { RootState } from '../../store';
import {
  fetchPackingListByIdStart,
  createPackingListStart,
  updatePackingListStart,
  fetchDropdownsStart,
  fetchDetailsStart,
  addDetailStart,
  updateDetailStart,
  deleteDetailStart,
  populateShippingFromLocation,
  setValidationErrors,
  clearValidationErrors,
  clearError,
  setEditingDetail,
} from '../../store/reducers/packingListSlice';
import {
  PackingList,
  PackingListDetail,
  CreatePackingListRequest,
} from '../../services/packingListApi';
import { PDFExportService } from '../../services/pdfExportService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`packing-list-tabpanel-${index}`}
      aria-labelledby={`packing-list-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

/**
 * PackingListEdit component for creating and editing packing lists
 * Features comprehensive form with shipping, export, and product management
 */
const PackingListEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = Boolean(id);

  const {
    selectedPackingList,
    selectedDetails,
    shipToLocations,
    products,
    loading,
    loadingDropdowns,
    detailsLoading,
    error,
    validationErrors,
    editingDetail,
    calculatedTotals,
  } = useSelector((state: RootState) => state.packingList);

  // Local state
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Partial<PackingList>>({
    PackingListNum: '',
    PackingListName: '',
    Number: '',
    RevDate: new Date().toISOString().slice(0, 10),
    Active: true,
    ShipFrom: '',
    ShipFromCo: '',
    ShipFromSt: '',
    ShipFromCity: '',
    ShipTo: '',
    ShipToCo: '',
    ShipToSt: '',
    ShipToCity: '',
    ShipToState: '',
    ShipToZIP: '',
    ShipToPhone: '',
    ShipToEmail: '',
    CountryOrigin: '',
    USSchedBCode: '',
    ECCN: '',
    ExportLicense: '',
    TypeContainer: '',
    NetWeight: 0,
    GrossWeight: 0,
    Volume: 0,
    TotalShippedQty: 0,
    TotalContainer: 0,
    RefDocName: '',
  });

  // Detail form state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<Partial<PackingListDetail>>({
    ProductId: '',
    Quantity: 0,
    Description: '',
    UnitPrice: 0,
    Active: true,
  });

  // Export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Load data on mount
  useEffect(() => {
    dispatch(fetchDropdownsStart());
    if (isEditMode && id) {
      dispatch(fetchPackingListByIdStart(id));
    }
  }, [dispatch, isEditMode, id]);

  // Update form data when selectedPackingList changes
  useEffect(() => {
    if (selectedPackingList) {
      setFormData(selectedPackingList);
    }
  }, [selectedPackingList]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field: keyof PackingList) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'number' 
      ? parseFloat(event.target.value) || 0 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (field: keyof PackingList) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleShipToLocationChange = (
    field: 'shipFrom' | 'shipTo' | 'billTo'
  ) => (event: React.ChangeEvent<{ value: unknown }>) => {
    const locationId = event.target.value as string;
    if (locationId) {
      dispatch(populateShippingFromLocation({ field, locationId }));
    }
  };

  const handleSave = async () => {
    dispatch(clearValidationErrors());
    
    try {
      if (isEditMode && id) {
        dispatch(updatePackingListStart({ ...formData, id }));
      } else {
        const createData: CreatePackingListRequest = {
          PackingListNum: formData.PackingListNum!,
          PackingListName: formData.PackingListName!,
          Number: formData.Number!,
          ShipFrom: formData.ShipFrom!,
          ShipFromCo: formData.ShipFromCo!,
          ShipFromSt: formData.ShipFromSt!,
          ShipFromCity: formData.ShipFromCity!,
          ShipTo: formData.ShipTo!,
          ShipToCo: formData.ShipToCo!,
          ShipToSt: formData.ShipToSt!,
          ShipToCity: formData.ShipToCity!,
          ShipToState: formData.ShipToState!,
          ShipToZIP: formData.ShipToZIP!,
          ShipToPhone: formData.ShipToPhone!,
          ShipToEmail: formData.ShipToEmail!,
          CountryOrigin: formData.CountryOrigin!,
          USSchedBCode: formData.USSchedBCode!,
          ECCN: formData.ECCN!,
          ExportLicense: formData.ExportLicense!,
          TypeContainer: formData.TypeContainer!,
          NetWeight: formData.NetWeight!,
          GrossWeight: formData.GrossWeight!,
          Volume: formData.Volume!,
          TotalShippedQty: formData.TotalShippedQty!,
          TotalContainer: formData.TotalContainer!,
          RefDocName: formData.RefDocName!,
        };
        dispatch(createPackingListStart(createData));
      }
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleCancel = () => {
    navigate('/packing-lists');
  };

  // Product Detail Management
  const handleAddDetail = () => {
    setCurrentDetail({
      ProductId: '',
      Quantity: 0,
      Description: '',
      UnitPrice: 0,
      Active: true,
    });
    setDetailDialogOpen(true);
  };

  const handleEditDetail = (detail: PackingListDetail) => {
    setCurrentDetail(detail);
    setDetailDialogOpen(true);
  };

  const handleDeleteDetail = (detail: PackingListDetail) => {
    if (detail.id) {
      dispatch(deleteDetailStart(detail.id));
    }
  };

  const handleSaveDetail = () => {
    if (!selectedPackingList?.id) return;

    const detailData = {
      ...currentDetail,
      PackingListId: selectedPackingList.id,
      TotalPrice: (currentDetail.Quantity || 0) * (currentDetail.UnitPrice || 0),
    };

    if (currentDetail.id) {
      dispatch(updateDetailStart(detailData as PackingListDetail));
    } else {
      dispatch(addDetailStart({
        packingListId: selectedPackingList.id,
        detail: detailData as Omit<PackingListDetail, 'id' | 'PackingListId'>,
      }));
    }

    setDetailDialogOpen(false);
  };

  const handleDetailInputChange = (field: keyof PackingListDetail) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'number' 
      ? parseFloat(event.target.value) || 0 
      : event.target.value;
    
    setCurrentDetail(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProductSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    const productId = event.target.value as string;
    const product = products.find(p => p.id === productId);
    
    if (product) {
      setCurrentDetail(prev => ({
        ...prev,
        ProductId: productId,
        Description: product.Description,
        Product: product,
      }));
    }
  };

  // PDF Export
  const handlePrintExport = () => {
    setExportDialogOpen(true);
  };

  const handleExportOption = async (exportType: 'simple' | 'professional') => {
    if (!selectedPackingList) return;

    setExportingPdf(true);
    try {
      const printData = {
        packingList: selectedPackingList,
        details: selectedDetails,
        companyLogo: '/images/otm-lo.png',
      };

      if (exportType === 'simple') {
        PDFExportService.generateSimplePDF(printData);
      } else {
        await PDFExportService.downloadProfessionalPDF(printData);
      }

      setExportDialogOpen(false);
    } catch (error: any) {
      console.error('Export failed:', error);
    } finally {
      setExportingPdf(false);
    }
  };

  if (loadingDropdowns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" style={{ marginLeft: '1rem' }}>
          Loading Packing List...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Packing List' : 'Add New Packing List'}
        </Typography>
        <Box display="flex" style={{ gap: '8px' }}>
          {isEditMode && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintExport}
              disabled={loading}
            >
              Print/Export
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {validationErrors.length > 0 && (
        <Paper style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: '#ffebee' }}>
          <Typography variant="h6" color="error">Validation Errors:</Typography>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index}>
                <Typography variant="body2" color="error">{error}</Typography>
              </li>
            ))}
          </ul>
        </Paper>
      )}

      {/* Tabbed Interface */}
      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Basic Information" icon={<BusinessIcon />} />
          <Tab label="Shipping Details" icon={<ShippingIcon />} />
          <Tab label="Export Information" icon={<ExportInfoIcon />} />
          <Tab label="Product Details" icon={<ProductsIcon />} />
          <Tab label="Summary" icon={<ReportIcon />} />
        </Tabs>

        {/* Basic Information Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Packing List Number"
                variant="outlined"
                value={formData.PackingListNum || ''}
                onChange={handleInputChange('PackingListNum')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer PO Number"
                variant="outlined"
                value={formData.Number || ''}
                onChange={handleInputChange('Number')}
                required
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Packing List Name"
                variant="outlined"
                value={formData.PackingListName || ''}
                onChange={handleInputChange('PackingListName')}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Revision Date"
                type="date"
                variant="outlined"
                value={formData.RevDate || ''}
                onChange={handleInputChange('RevDate')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Document"
                variant="outlined"
                value={formData.RefDocName || ''}
                onChange={handleInputChange('RefDocName')}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Shipping Details Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            {/* Ship From Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Ship From</Typography>
              <Divider style={{ marginBottom: '1rem' }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select Ship From Location</InputLabel>
                <Select
                  value=""
                  onChange={handleShipToLocationChange('shipFrom')}
                  label="Select Ship From Location"
                >
                  <MenuItem value="">Select Location...</MenuItem>
                  {shipToLocations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.AttName} - {location.AttCity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                value={formData.ShipFrom || ''}
                onChange={handleInputChange('ShipFrom')}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company"
                variant="outlined"
                value={formData.ShipFromCo || ''}
                onChange={handleInputChange('ShipFromCo')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street"
                variant="outlined"
                value={formData.ShipFromSt || ''}
                onChange={handleInputChange('ShipFromSt')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={formData.ShipFromCity || ''}
                onChange={handleInputChange('ShipFromCity')}
                required
              />
            </Grid>

            {/* Ship To Section */}
            <Grid item xs={12} style={{ marginTop: '2rem' }}>
              <Typography variant="h6" gutterBottom>Ship To</Typography>
              <Divider style={{ marginBottom: '1rem' }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select Ship To Location</InputLabel>
                <Select
                  value=""
                  onChange={handleShipToLocationChange('shipTo')}
                  label="Select Ship To Location"
                >
                  <MenuItem value="">Select Location...</MenuItem>
                  {shipToLocations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.AttName} - {location.AttCity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                value={formData.ShipTo || ''}
                onChange={handleInputChange('ShipTo')}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company"
                variant="outlined"
                value={formData.ShipToCo || ''}
                onChange={handleInputChange('ShipToCo')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street"
                variant="outlined"
                value={formData.ShipToSt || ''}
                onChange={handleInputChange('ShipToSt')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={formData.ShipToCity || ''}
                onChange={handleInputChange('ShipToCity')}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="State"
                variant="outlined"
                value={formData.ShipToState || ''}
                onChange={handleInputChange('ShipToState')}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="ZIP Code"
                variant="outlined"
                value={formData.ShipToZIP || ''}
                onChange={handleInputChange('ShipToZIP')}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                value={formData.ShipToPhone || ''}
                onChange={handleInputChange('ShipToPhone')}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                value={formData.ShipToEmail || ''}
                onChange={handleInputChange('ShipToEmail')}
                required
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Export Information Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country of Origin"
                variant="outlined"
                value={formData.CountryOrigin || ''}
                onChange={handleInputChange('CountryOrigin')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="US Schedule B Code"
                variant="outlined"
                value={formData.USSchedBCode || ''}
                onChange={handleInputChange('USSchedBCode')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ECCN"
                variant="outlined"
                value={formData.ECCN || ''}
                onChange={handleInputChange('ECCN')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Export License"
                variant="outlined"
                value={formData.ExportLicense || ''}
                onChange={handleInputChange('ExportLicense')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Type of Container"
                variant="outlined"
                value={formData.TypeContainer || ''}
                onChange={handleInputChange('TypeContainer')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Containers (Units)"
                type="number"
                variant="outlined"
                value={formData.TotalContainer || 0}
                onChange={handleInputChange('TotalContainer')}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Net Weight (lb)"
                type="number"
                variant="outlined"
                value={formData.NetWeight || 0}
                onChange={handleInputChange('NetWeight')}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Gross Weight (lb)"
                type="number"
                variant="outlined"
                value={formData.GrossWeight || 0}
                onChange={handleInputChange('GrossWeight')}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Volume (L)"
                type="number"
                variant="outlined"
                value={formData.Volume || 0}
                onChange={handleInputChange('Volume')}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Total Shipped Quantity"
                type="number"
                variant="outlined"
                value={formData.TotalShippedQty || 0}
                onChange={handleInputChange('TotalShippedQty')}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Product Details Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Product Details</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddDetail}
              disabled={!isEditMode}
            >
              Add Product
            </Button>
          </Box>

          {!isEditMode && (
            <Paper style={{ padding: '2rem', textAlign: 'center', marginBottom: '1rem' }}>
              <Typography variant="body1" color="textSecondary">
                Save the packing list first to add product details.
              </Typography>
            </Paper>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell>Total Price</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : selectedDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No products added yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedDetails.map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {detail.Product?.Number || detail.ProductId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {detail.Description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {detail.Quantity} {detail.Product?.UnitOfMeasure || 'EA'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${detail.UnitPrice?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          ${detail.TotalPrice?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" style={{ gap: '4px' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditDetail(detail)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDetail(detail)}
                              color="secondary"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Summary Tab */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Packing List Summary
                  </Typography>
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Packing List Number
                    </Typography>
                    <Typography variant="body1">
                      {formData.PackingListNum || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Customer PO
                    </Typography>
                    <Typography variant="body1">
                      {formData.Number || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={formData.Active ? 'Active' : 'Inactive'}
                      color={formData.Active ? 'primary' : 'default'}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Product Totals
                  </Typography>
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Total Items
                    </Typography>
                    <Typography variant="h4">
                      {calculatedTotals.itemCount}
                    </Typography>
                  </Box>
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Total Quantity
                    </Typography>
                    <Typography variant="h5">
                      {calculatedTotals.totalQuantity}
                    </Typography>
                  </Box>
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Total Value
                    </Typography>
                    <Typography variant="h5">
                      ${calculatedTotals.totalValue.toFixed(2)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Shipping Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Ship From
                      </Typography>
                      <Typography variant="body2">
                        {formData.ShipFrom}<br />
                        {formData.ShipFromCo}<br />
                        {formData.ShipFromSt}<br />
                        {formData.ShipFromCity}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Ship To
                      </Typography>
                      <Typography variant="body2">
                        {formData.ShipTo}<br />
                        {formData.ShipToCo}<br />
                        {formData.ShipToSt}<br />
                        {formData.ShipToCity}, {formData.ShipToState} {formData.ShipToZIP}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Export Information
                      </Typography>
                      <Typography variant="body2">
                        Country: {formData.CountryOrigin}<br />
                        ECCN: {formData.ECCN}<br />
                        Weight: {formData.NetWeight} lb<br />
                        Volume: {formData.Volume} L
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Product Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentDetail.id ? 'Edit Product Detail' : 'Add Product Detail'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Product</InputLabel>
                <Select
                  value={currentDetail.ProductId || ''}
                  onChange={handleProductSelect}
                  label="Product"
                >
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.Number} - {product.Description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                value={currentDetail.Description || ''}
                onChange={handleDetailInputChange('Description')}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                variant="outlined"
                value={currentDetail.Quantity || 0}
                onChange={handleDetailInputChange('Quantity')}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                variant="outlined"
                value={currentDetail.UnitPrice || 0}
                onChange={handleDetailInputChange('UnitPrice')}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Total Price"
                variant="outlined"
                value={((currentDetail.Quantity || 0) * (currentDetail.UnitPrice || 0)).toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveDetail} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Packing List</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Choose your export format:
          </Typography>
          <Box display="flex" flexDirection="column" style={{ gap: '8px', marginTop: '1rem' }}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => handleExportOption('simple')}
              disabled={exportingPdf}
            >
              Simple Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={() => handleExportOption('professional')}
              disabled={exportingPdf}
            >
              Professional PDF
            </Button>
          </Box>
          {exportingPdf && (
            <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
              <CircularProgress size={20} style={{ marginRight: '8px' }} />
              <Typography variant="body2">Generating PDF...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exportingPdf}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => dispatch(clearError())}
        message={error}
      />
    </Box>
  );
};

export default PackingListEdit; 